/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { FormattedMessage } from 'react-intl';
import type { JSX } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { i18n } from './viewXlsMacroI18n.ts';
import { truncateFileName } from './viewXlsMacroParamsUtils.ts';

type NoticeBannerProps = {
	fileName: string;
	attachmentPageUrl: string;
	iconUrl: string;
};

const styles = cssMap({
	card: {
		position: 'relative',
		marginBottom: token('space.100'),
	},
	iconContainer: {
		position: 'absolute',
		height: '100%',
		width: '80px',
	},
	iconContent: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		margin: token('space.0'),
		height: '45px',
		width: '45px',
	},
	headlineContainer: {
		marginInlineStart: token('space.1000'),
	},
	subtitleContainer: {
		marginInlineStart: token('space.1000'),
	},
});

/**
 * Notice card shown above the Media Viewer when an Excel file's HTML artifact
 * cannot be retrieved. Prompts the user to re-upload the file to get the new
 * spreadsheet preview. Matches the legacy media_viewer_with_notice.vm template.
 */
export const NoticeBanner = ({
	fileName,
	attachmentPageUrl,
	iconUrl,
}: NoticeBannerProps): JSX.Element => {
	const truncated = truncateFileName(fileName);
	const isTruncated = truncated !== fileName;

	return (
		<div css={styles.card} data-testid="viewxls-notice-banner">
			<div css={styles.iconContainer}>
				<img css={styles.iconContent} src={iconUrl} alt="" />
			</div>
			<div css={styles.headlineContainer}>
				<Text as="p" weight="bold">
					<FormattedMessage {...i18n.noticeBannerHeadline} />
				</Text>
			</div>
			<div css={styles.subtitleContainer}>
				<Text as="p">
					<FormattedMessage
						{...i18n.noticeBannerBody}
						values={{
							filename: isTruncated ? (
								<span title={fileName}>{truncated}</span>
							) : (
								<span>{fileName}</span>
							),
							link: (
								<Anchor href={attachmentPageUrl}>
									<FormattedMessage {...i18n.noticeBannerLinkLabel} />
								</Anchor>
							),
						}}
					/>
				</Text>
			</div>
		</div>
	);
};
